/**
 * Audit record factories, signing input construction, and chain linkage.
 *
 * Phase 1 produces `decision = 'observed'` records (Discovery Mode) via
 * `createObservedRecord`. Phase 2 (ADR-0029 §4.1) adds
 * `createTransactionalRecord` for the four post-verification decisions
 * (`'approved' | 'rejected' | 'verification_rejected' | 'rejected_post_verify'`).
 * Escalation outcomes (`'escalated_*'`) are written by the
 * escalation-resolver worker at operator resolution time, not by the
 * mandate-replication queue consumer; the factory accepts them too so the
 * resolver can reuse the same shape.
 *
 * `signAndLink` composes the chain hash (`prev_record_hash`), computes
 * the signing input, calls `KmsClient.sign`, and returns a record whose
 * `merkle_root` is still the all-zeros "pending" sentinel — the per-day batch
 * computer (`computeMerkleBatch`) fills that field in later. This two-step
 * design is the load-bearing decision in ADR-0004 §4: it lets the edge emit
 * a chain-linked, signed record synchronously while origin attaches the
 * Merkle root asynchronously when the batch closes.
 *
 * See ADR-0004 §3, §4 and ADR-0029 §4.1.
 */
import { bytesToBase64Url, canonicalize, sha256OfString, utf8Bytes, } from './canonical.js';
/**
 * Sentinel value for `merkle_root` between sign-and-link time and batch
 * close. Per ADR-0004 §"Open questions", we use the all-zeros base64url
 * SHA-256 (32 zero bytes → 43 base64url chars). The implementer brief
 * authorized this choice over the literal string `'pending'` because the
 * value is the same length and character set as a real root, so consumers
 * cannot accidentally treat it as a different type — but the all-zero bit
 * pattern is computationally unreachable as a real SHA-256 output (a
 * pre-image attack would be required), making it safe to compare against.
 */
export const PENDING_MERKLE_ROOT = 'AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA';
/**
 * Sentinel value for `request_hash` / `response_hash` on records that
 * never had a captured payload (discovery embed beacons, verification
 * rejections, sanitized rejections, escalation outcomes). Same shape
 * as {@link PENDING_MERKLE_ROOT} — 43 zero-base64url chars. A real
 * SHA-256 output cannot collide with the all-zero bit pattern (a
 * pre-image attack would be required), so verifiers cannot confuse
 * the sentinel with an attacker-supplied legitimate hash.
 *
 * ADR-0059 §5.2.
 */
export const EMPTY_PAYLOAD_HASH = 'AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA';
/**
 * ADR-0156 §6.1 — the single gate that decides whether a record carries
 * a REAL (meaningful) payload hash, as opposed to the `EMPTY_PAYLOAD_HASH`
 * sentinel or a NULL/absent column. Returns `true` ONLY for a 43-char
 * base64url string that is not the sentinel.
 *
 * LOAD-BEARING: this is the gate EVERY read-side projection of an
 * `audit_record` row must use to decide whether to put `intent_summary`
 * onto the signed record shape. `intent_summary` is in `SIGNED_AUDIT_FIELDS`,
 * so it participates in `signingInputBytes`, `chainHashOf`/`recordHashBytes`,
 * AND `leafHashOfRecord` (the Merkle leaf). If ANY loader that feeds those
 * functions disagrees about whether to include `intent_summary`, the chain
 * link or the Merkle root it computes will diverge from what the verifier
 * computes — silently, and only for the second-and-later record on a chain.
 * There are FOUR such projections that MUST agree:
 *   1. `createTransactionalRecord` (the write/sign path, packages/audit)
 *   2. `rowToRecord` (bundle export / verify path, audit-bundle.ts)
 *   3. `rowToAuditRecord` (chain-head loader, process-mandate-draft.ts)
 *   4. `rowToAuditRecord` (Merkle-leaf loader, batches/closer.ts)
 * All four gate on THIS predicate. Adding a fifth reader of `audit_record`
 * that feeds a hash function MUST route through here.
 */
export function isRealPayloadHash(hash) {
    return typeof hash === 'string' && hash.length === 43 && hash !== EMPTY_PAYLOAD_HASH;
}
/**
 * ADR-0059 §8.1 — deploy-boundary timestamp for the
 * `request_hash` / `response_hash` signed-scope widening.
 *
 * NULL-presence on the record value (equivalently: the DB column)
 * is the load-bearing discriminator; the verifier
 * (`signingInputBytes`, `recordHashBytes`, `verifyAuditRecord`)
 * strips `request_hash` and `response_hash` from the canonical
 * signing input when EITHER field is absent / undefined on the
 * record. This is the safe back-compat path because a NULL column
 * cannot have been part of the bytes the OLD signer produced. The
 * boundary timestamp below is documented as belt-and-suspenders —
 * the verifier does NOT consult it in the NULL-presence dispatch
 * path; it is a release-time pin set to the actual production-deploy
 * timestamp when the §8.3 step 3 code release goes live, so an
 * operational reader of the chain can quickly identify the
 * pre/post-widening boundary without inspecting every row.
 *
 * RELEASE TIME: when the code release lands in production, replace
 * the string below with the actual deploy ISO timestamp via a
 * follow-up commit. Until then the value is the ADR amendment date.
 */
export const AUDIT_RECORD_PAYLOAD_HASH_INTRODUCED_AT = '2026-06-04T00:00:00Z';
/**
 * Canonical chain-class literals. Exported (rather than relying on each
 * caller to spell the string) so the lock-key + chain-class arguments
 * the order-approval handler, the SPT-enrichment handler, the mandate
 * consumer, and the batch closer pass into Postgres + into `signAndLink`
 * are bytewise identical. Drift here re-introduces the
 * audit-chain-fork hazard ADR-0139 §"Audit-chain integration fix"
 * names explicitly.
 *
 * Mirrors the existing `TRANSACTIONAL_CHAIN_CLASS` literal in
 * `services/api/src/queue/process-mandate-draft.ts:1843`; future
 * implementer cleanup imports from here instead of redefining locally.
 */
export const DISCOVERY_CHAIN_CLASS = 'discovery';
export const TRANSACTIONAL_CHAIN_CLASS = 'transactional';
/**
 * Map an `AuditRecord.decision` value to its chain's decision class.
 * `'observed'` is discovery; every other decision is transactional.
 *
 * Back-compat helper — every CHAIN-class consumer that does NOT pass an
 * explicit `chain_class` derives it via this function (the legacy
 * invariant: every existing row has
 * `decisionClassOf(decision) === chain_class`). ADR-0139 Slice 2 admits
 * a legitimate exception: an enrichment row with `decision = 'observed'`
 * on the `'transactional'` chain. Callers that write such rows MUST
 * thread the explicit `chain_class` parameter through `signAndLink` /
 * `verifyAuditRecord` (the additive widening below) so chain identity
 * is NOT derived from decision identity.
 */
export function decisionClassOf(decision) {
    return decision === 'observed' ? 'discovery' : 'transactional';
}
/**
 * Build an unsigned discovery-mode audit record. The caller supplies an
 * `AuditRecordId` (ULID, minted upstream) and an `evaluated_at` timestamp;
 * the package never calls `Date.now()` or generates IDs.
 */
export function createObservedRecord(input, record_id) {
    // ADR-0059 §5 — both hash fields are required on a NEW record (the
    // widening landed; new code writes both). When the caller did not
    // supply a hash (e.g. a discovery embed beacon with no payload to
    // hash), default to the EMPTY_PAYLOAD_HASH sentinel. Pre-widening
    // ROWS already on disk are NULL — they never pass through this
    // factory because they were written under the OLD code release.
    const out = {
        record_id,
        site_id: input.site_id,
        evaluated_at: input.evaluated_at,
        policy_version: input.policy_version,
        rules_evaluated: input.rules_evaluated,
        decision: 'observed',
        retention_class: input.retention_class,
        request_hash: input.request_hash ?? EMPTY_PAYLOAD_HASH,
        response_hash: input.response_hash ?? EMPTY_PAYLOAD_HASH,
    };
    // One runtime invariant — guards against a future code change that
    // widens this factory to the transactional decision space without
    // updating the type.
    if (out.decision !== 'observed') {
        throw new Error('createObservedRecord: invariant violated — decision must be "observed"');
    }
    return out;
}
/**
 * Build an unsigned transactional audit record (ADR-0029 §4.1). The
 * factory does not mint IDs or timestamps — both are caller-supplied so
 * the package stays pure and deterministic under test. Wider input
 * validation belongs to the queue consumer that constructs this shape.
 *
 * Phase 1's `createObservedRecord` invariant ("decision must be
 * 'observed'") is mirrored here on the transactional side: the factory
 * rejects any decision value that is not one of the four transactional
 * decisions. `'observed'` and the two escalation outcomes are deliberate
 * write-path mismatches — discovery uses `createObservedRecord`, and
 * escalation resolution writes a different shape (operator_id +
 * operator_decision_at) through the resolver worker.
 *
 * SIGNED-SCOPE FORWARD-COMPAT (ADR-0156 §7 review): `operator_id`,
 * `operator_decision_at`, `identification_match`, and `user_agent` are in
 * `SIGNED_AUDIT_FIELDS` and are already projected (gated on `!= null`) by
 * ALL read-side loaders — the bundle route, the chain-head loader, and the
 * Merkle-leaf closer (see `isRealPayloadHash`'s "four projections MUST
 * agree" note). They are NULL on every record today because no writer sets
 * them. When a future slice starts signing any of them, it MUST thread the
 * value through THIS factory (widen `TransactionalRecordInput`) so the
 * WRITE-side canonical form includes it. A writer that INSERTs the column
 * directly, bypassing the factory, would leave the signed envelope without
 * the field while the read-side loaders canonicalise it back IN → the
 * record fails `verifyAuditRecord` with `signature_invalid`.
 */
export function createTransactionalRecord(input, record_id) {
    // The factory output is the wider `UnsignedAuditRecord`; the type
    // already widened in this file's Phase 2 update.
    //
    // ADR-0156 §3.3 + §6.1 — `intent_summary` rides on the SIGNED record
    // ONLY when the caller supplies it (i.e. the APPROVED / escalation-
    // outcome call site with a real `request_hash`). Absent → omitted
    // from the record entirely so canonicalisation drops the key (JCS
    // does not emit absent keys). This matches the `rowToRecord`
    // widening gate at bundle-read time (which only sets
    // `intent_summary` on the signed projection when
    // `request_hash !== EMPTY_PAYLOAD_HASH`) — the two branches are the
    // load-bearing byte-equality pair.
    // ADR-0158 §3.3 — invariant guards on the operator identity fields.
    //
    // (a) operator_id / operator_decision_at may appear ONLY on a
    //     HITL-resolved escalation-outcome record. Any other decision
    //     class carrying them is a cross-wiring bug; throw so a test
    //     catches it at record-build time, not `signature_invalid` at
    //     bundle-verify time. Mirrors the escalation-only shape of the
    //     `escalation_resolution` draft tag.
    //
    // (b) On an escalation-outcome record, BOTH or NEITHER — never one
    //     without the other. A half-widened shape (only `operator_id`
    //     or only `operator_decision_at`) would produce a canonical form
    //     with an asymmetric key set and open the door to a byte-shape
    //     drift no test exercises.
    const has_operator_id = input.operator_id !== undefined;
    const has_operator_decision_at = input.operator_decision_at !== undefined;
    if ((has_operator_id || has_operator_decision_at)
        && input.decision !== 'escalated_approved'
        && input.decision !== 'escalated_rejected') {
        throw new Error(`createTransactionalRecord: operator_id / operator_decision_at may only `
            + `be set on 'escalated_approved' | 'escalated_rejected' decisions; got `
            + `decision="${String(input.decision)}". ADR-0158 §3.3 invariant.`);
    }
    if (has_operator_id !== has_operator_decision_at) {
        throw new Error(`createTransactionalRecord: operator_id and operator_decision_at must `
            + `be both set or both unset (half-widened shape rejected). `
            + `ADR-0158 §3.3 half-widened-shape guard.`);
    }
    const base = {
        record_id,
        site_id: input.site_id,
        evaluated_at: input.evaluated_at,
        policy_version: input.policy_version,
        rules_evaluated: input.rules_evaluated,
        decision: input.decision,
        retention_class: input.retention_class,
        request_hash: input.request_hash ?? EMPTY_PAYLOAD_HASH,
        response_hash: input.response_hash ?? EMPTY_PAYLOAD_HASH,
        ...(input.mandate_id !== undefined ? { mandate_id: input.mandate_id } : {}),
        ...(input.intent_summary !== undefined
            ? { intent_summary: input.intent_summary }
            : {}),
        // ADR-0158 §3.3 — operator identity rides on the signed shape
        // ONLY when both fields were supplied by the caller. Absent →
        // JCS drops the keys from the canonical form (historical / non-
        // HITL / sentinel-resolved records sign byte-identically to what
        // they signed before this slice).
        ...(has_operator_id && has_operator_decision_at
            ? {
                operator_id: input.operator_id,
                operator_decision_at: input.operator_decision_at,
            }
            : {}),
    };
    if (base.decision !== 'approved'
        && base.decision !== 'rejected'
        // ADR-0094 Slice 2b — HITL resolution outcomes are valid transactional
        // decisions written by the escalation resolver.
        && base.decision !== 'escalated_approved'
        && base.decision !== 'escalated_rejected'
        && base.decision !== 'verification_rejected'
        && base.decision !== 'rejected_post_verify'
        // ADR-0157 (Bucket B Slice 2) — RAIL-NEUTRAL settlement outcome.
        && base.decision !== 'settled_succeeded'
        && base.decision !== 'settled_refunded'
        // ADR-0174 §2 (Slice 1) — WooCommerce PAYMENT-LINK HANDOFF outcome.
        && base.decision !== 'handoff_pending') {
        throw new Error(`createTransactionalRecord: invariant violated — decision must be a valid transactional decision, got "${String(base.decision)}"`);
    }
    return base;
}
/**
 * Compute the genesis chain-link hash for a (site, decision_class) pair.
 *
 * The prefix `sill-audit-genesis-v1` is the versioned domain separator
 * (ADR-0004 §"Open questions" — versioned to allow a future change without
 * breaking deployed chains). Two chains on the same site cannot collide
 * because their decision_class differs; two sites cannot collide because
 * their site_id differs.
 */
export function genesisPrevRecordHash(site_id, decision_class) {
    const seed = `sill-audit-genesis-v1|${site_id}|${decision_class}`;
    return bytesToBase64Url(sha256OfString(seed));
}
/**
 * Shared canonical-form derivation for the chain-link hash and the Merkle
 * leaf hash. Normalizes `merkle_root` to {@link PENDING_MERKLE_ROOT} before
 * canonifying so the two derivations are byte-identical regardless of
 * whether the record's `merkle_root` still carries the pending sentinel
 * or has had the real batch root written back into it at end-of-day.
 *
 * Per ADR-0004 §5: "the chain hash and the Merkle leaf hash are byte-
 * identical." Both `chainHashOf` and `leafHashOfRecord` MUST route
 * through this helper; drift between the two would break verification
 * once origin closes a batch and rewrites `merkle_root` on every record.
 *
 * Exported (rather than module-private) so the Merkle layer can import
 * it without re-implementing the normalization step.
 */
/**
 * Canonical-form allow-list — the closed set of keys that may appear on an
 * `AuditRecord` at signing/hashing time. Acts as a runtime fail-closed
 * guard against accidental widening of the canonical form (e.g. a future
 * refactor that spreads a wire-shaped object carrying ADR-0034's
 * `visit_count` / `last_visit_at` into the signing path, or any
 * `Object.assign` mutation that adds an unknown field). The JCS
 * canonicaliser walks every enumerable own property of the input object
 * regardless of TypeScript narrowing, so this check is the only defence
 * against silent inputs widening.
 *
 * Keep in sync with the `AuditRecord` interface in
 * `packages/types/src/audit.ts`. If a new field is intentionally added
 * to the signed envelope, add it here AND document the addition in an
 * ADR amendment. ADR-0034 security review MAJOR-1 mitigation.
 */
const SIGNED_AUDIT_FIELDS = new Set([
    'record_id',
    'mandate_id',
    'site_id',
    'evaluated_at',
    'policy_version',
    'rules_evaluated',
    'decision',
    'operator_id',
    'operator_decision_at',
    'envelope_signature',
    'prev_record_hash',
    'merkle_root',
    'retention_class',
    'identification_match',
    'user_agent',
    // ADR-0156 §3.3 + §6.1 — populated on the SIGNED record ONLY at the
    // APPROVED / escalation-outcome queue-consumer call site (with a
    // real, non-sentinel `request_hash`). The buyer-stripped shape is
    // byte-identical to `audit_record.intent_summary` JSONB (mirrors the
    // ADR-0064 `projectIntentSummaryForPersist` output). Sanitized
    // (rejected_post_verify / verification_rejected) rows omit it; the
    // bundle route's `rowToRecord` gate (`request_hash !== EMPTY_PAYLOAD_HASH`)
    // preserves byte-equality with what those rows signed at write time.
    'intent_summary',
    // ADR-0059 §5.3 — two fixed-size base64url SHA-256 hashes binding
    // the audit envelope to the stored request + response content. The
    // values are computed BEFORE signing and durable on the audit_record
    // row; an `EMPTY_PAYLOAD_HASH` sentinel covers records with no
    // captured payload so the canonical signing-input shape is uniform.
    'request_hash',
    'response_hash',
]);
function assertCanonicalShape(record, context) {
    for (const key of Object.keys(record)) {
        if (!SIGNED_AUDIT_FIELDS.has(key)) {
            throw new Error(`${context}: unexpected field "${key}" on AuditRecord input. `
                + `Either add it to SIGNED_AUDIT_FIELDS (and document the addition in `
                + `an ADR amendment) or strip it before signing/hashing. `
                + `ADR-0034 security review MAJOR-1 fail-closed guard.`);
        }
    }
}
/**
 * ADR-0059 §8 — NULL-presence dispatch for `request_hash` / `response_hash`.
 *
 * The signed-scope widening from ADR-0059 added two new fields. Records
 * written BEFORE that widening landed do NOT carry the fields (the DB
 * column is NULL); their `envelope_signature` was computed over a
 * canonical form that omitted them. To keep those records verifying
 * green under the new code, the signer + chain hash + Merkle leaf
 * derivations all run through this projection helper, which strips
 * the two fields when EITHER is absent / null / undefined on the
 * input. Post-widening records carry both (either the sentinel or a
 * real hash) and the projection is a no-op.
 *
 * NULL-presence is the discriminator (not the
 * `AUDIT_RECORD_PAYLOAD_HASH_INTRODUCED_AT` boundary timestamp) because
 * a NULL column cannot have been part of the bytes the old signer
 * produced — that is structural, not advisory.
 *
 * The projection preserves every other key untouched and runs BEFORE
 * `assertCanonicalShape` so the fail-closed guard still catches any
 * future widening of the record shape.
 */
function projectForCanonicalForm(record) {
    const has_request = record.request_hash !== undefined && record.request_hash !== null;
    const has_response = record.response_hash !== undefined && record.response_hash !== null;
    // Both present → canonicalise WITH them (post-widening shape).
    if (has_request && has_response)
        return record;
    // Either absent / null → strip BOTH from the canonical form so the
    // bytes match what the pre-widening signer produced. We never sign
    // a half-widened shape (one hash present, one absent) — that would
    // be a third canonical form with no precedent.
    const { request_hash: _r, response_hash: _s, ...rest } = record;
    void _r;
    void _s;
    return rest;
}
export function recordHashBytes(record) {
    assertCanonicalShape(record, 'recordHashBytes');
    const projected = projectForCanonicalForm(record);
    const normalized = {
        ...projected,
        merkle_root: PENDING_MERKLE_ROOT,
    };
    return sha256OfString(canonicalize(normalized));
}
/**
 * Compute `prev_record_hash` for the next record in the chain.
 *
 * The hash input is `SHA-256(canonify(prev_record))` with `merkle_root`
 * normalized to the pending sentinel (see {@link recordHashBytes}). This
 * matches the Merkle leaf hash for the same record by construction, which
 * is the invariant ADR-0004 §5 relies on so that chain verification still
 * passes after the per-day batch closer writes the real Merkle root back
 * into every record.
 */
export function chainHashOf(prev_record) {
    return bytesToBase64Url(recordHashBytes(prev_record));
}
/**
 * The canonical signing input for an audit record's `envelope_signature`.
 *
 * Drops `envelope_signature` and `merkle_root`; keeps every other field —
 * including the populated `prev_record_hash`. This is the load-bearing
 * decision that lets the chain link be verifiable from the signed envelope
 * alone (the verifier in §6 recomputes this same input).
 *
 * Per ADR-0004 §4 step 2 and §6 step 1. Note: §3 paragraph 4 of the ADR
 * describes the signing input as "everything except envelope_signature,
 * prev_record_hash, and merkle_root" but that conflicts with §4 step 2 and
 * §6 step 1, both of which include `prev_record_hash`. The verifier is the
 * load-bearing reference (chain link integrity requires the previous-hash
 * to be inside the signed envelope), so we follow §4/§6 here.
 */
export function signingInputBytes(recordWithPrevHash) {
    // Fail-closed guard against accidental widening (ADR-0034 security
    // MAJOR-1). The JCS canonicaliser walks every enumerable own property
    // regardless of TypeScript narrowing; without this guard, a future
    // refactor that put a sibling-table field on the signing input would
    // silently drift the envelope_signature scope.
    assertCanonicalShape(recordWithPrevHash, 'signingInputBytes');
    // ADR-0059 §8 NULL-presence dispatch — strip request_hash /
    // response_hash from the canonical form when either is absent on
    // the input. Pre-widening records carry NULL on the DB columns
    // and produce wire values with both keys absent; we MUST sign
    // them under the pre-widening canonical shape so the existing
    // envelope_signature continues to verify.
    const projected = projectForCanonicalForm(recordWithPrevHash);
    return utf8Bytes(canonicalize(projected));
}
/**
 * Compute `prev_record_hash`, build the signing input, ask the KMS to sign,
 * and return a record with `envelope_signature` and `prev_record_hash`
 * populated. `merkle_root` is left as the all-zeros pending sentinel; the
 * batch computer fills it in later.
 *
 * Pass `prev_record = null` for the genesis record of a (site,
 * decision_class) chain pair.
 *
 * The optional `options.chain_class` overrides the chain identity
 * (otherwise derived from `decisionClassOf(unsigned.decision)`). See
 * {@link SignAndLinkOptions} for the canonical use case (ADR-0139
 * Slice 2 enrichment rows: `decision = 'observed'` on the
 * `'transactional'` chain). Omitting it preserves byte-for-byte
 * back-compat for every existing caller.
 */
export async function signAndLink(unsigned, prev_record, kms, options) {
    // Explicit chain_class when supplied; otherwise derive from decision
    // (the legacy invariant — preserves byte-for-byte back-compat for
    // every Phase-1 discovery + Phase-2 transactional caller).
    const decision_class = options?.chain_class ?? decisionClassOf(unsigned.decision);
    const prev_record_hash = prev_record === null
        ? genesisPrevRecordHash(unsigned.site_id, decision_class)
        : chainHashOf(prev_record);
    // The signing input excludes envelope_signature and merkle_root but
    // INCLUDES the just-computed prev_record_hash so the chain link is
    // covered by the signature.
    const recordForSigning = {
        ...unsigned,
        prev_record_hash,
    };
    const signing_input = signingInputBytes(recordForSigning);
    const envelope_signature = await kms.sign(signing_input);
    // Compose the final record. merkle_root is the pending sentinel until
    // computeMerkleBatch() runs at end-of-day.
    const signed = {
        ...recordForSigning,
        envelope_signature,
        merkle_root: PENDING_MERKLE_ROOT,
    };
    return signed;
}
//# sourceMappingURL=record.js.map